import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  ToolCategory,
  IResult,
  IRephrasedResult,
  ResultType,
  IValues,
} from '../../utils/common/types';
import { handleScroll } from '../../utils/common/scroll';
import { FORM_INITIAL_VALUES } from '../../constants/form-values';

type Store = {
  formValues: IValues;
  createPageCategoryIndex: number;
  results: Record<ToolCategory, IResult[]>;
  recents: IResult[];
  resultsAreGenerating: boolean;
  moreResultsAreGenerating: boolean;
  validationError: boolean;
  genericError: string | JSX.Element | JSX.Element[];
  setFormValue: ({ name, value }: { name: string; value: string }) => void;
};

type StoreActions = {
  setCreatePageCategoryIndex: (i: number) => void;
  setResultsAreGenerating: (value: boolean) => void;
  setMoreResultsAreGenerating: (value: boolean) => void;
  addResults: (item: { category: ToolCategory; newResults: IResult[]; replace?: boolean }) => void;
  removeResult({ category, id }: { category: ToolCategory; id: string }): void;
  addRephrases: (item: {
    category: ToolCategory;
    rephrasedResults: IRephrasedResult[];
    id: string;
  }) => void;
  setIsRephrasing: (item: { category: ToolCategory; id: string; value: boolean }) => void;
  addRecents: (item: { category: ToolCategory; id: string }) => void;
  showValidationError: (error: boolean) => void;
  showGenericError: (error: string | JSX.Element | JSX.Element[]) => void;
  setFormValues: (values: IValues) => void;
};

const initialState = {
  formValues: FORM_INITIAL_VALUES,
  createPageCategoryIndex: 0,
  results: {},
  recents: [],
  resultsAreGenerating: false,
  moreResultsAreGenerating: false,
  validationError: false,
  genericError: '',
};

function findResultToBeAddedToRecents(results: IResult[], recents: IResult[], id: string) {
  const resultsAndRecents = [...(results || []), ...recents];
  const toAdd = resultsAndRecents.find((res) => res.id === id);

  if (toAdd) {
    return toAdd;
  }

  const rephrased = resultsAndRecents.reduce<IRephrasedResult[]>(
    (all, current) => all.concat(current.rephrasedResults),
    [],
  );

  return rephrased.find((res) => res.id === id);
}

function textAddedToResults(results: IResult[], res: IResult | IRephrasedResult) {
  const withRephrases = results.reduce<IRephrasedResult[]>(
    (all, current) => all.concat(current.rephrasedResults),
    [...results],
  );

  return Boolean(withRephrases.find((recent) => recent.id === res?.id));
}

const useStore = create<Store & StoreActions>()(
  persist(
    (set, get) => {
      let genericErrorTimeout: ReturnType<typeof setTimeout>;

      return {
        ...initialState,

        setCreatePageCategoryIndex: (createPageCategoryIndex) => {
          set({ createPageCategoryIndex });
        },

        setResultsAreGenerating: (resultsAreGenerating) => {
          set({ resultsAreGenerating });
        },

        setMoreResultsAreGenerating: (moreResultsAreGenerating) => {
          set({ moreResultsAreGenerating });
        },

        addResults: ({ category, newResults, replace }) => {
          const { results } = get();
          const categoryResults = results[category];

          if (!replace) {
            newResults = categoryResults.concat(newResults);
          }

          set({
            results: {
              ...results,
              [category]: newResults,
            },
          });
        },

        removeResult: ({ category, id }) => {
          const { results } = get();
          const categoryResults = results[category] || [];
          const newResultCategory = categoryResults.filter((res) => res.id !== id);

          set({
            results: {
              ...results,
              [category]: newResultCategory,
            },
          });
        },

        addRephrases: ({ category, rephrasedResults, id }) => {
          const { results, recents } = get();
          const resultCategory = results[category];
          const newResultCategory = resultCategory.map((res) => {
            return res.id === id ? { ...res, rephrasedResults } : res;
          });
          const newRecents = recents.map((res) => {
            return res.id === id ? { ...res, rephrasedResults } : res;
          });

          set({
            results: {
              ...results,
              [category]: newResultCategory,
            },
            recents: newRecents,
          });
        },

        setIsRephrasing: ({ category, id, value }) => {
          const { results, recents } = get();
          const resultCategory = results[category];
          const newResultCategory = resultCategory.map((res) => {
            return res.id === id ? { ...res, isRephrasing: value } : res;
          });
          const newRecents = recents.map((res) => {
            return res.id === id ? { ...res, isRephrasing: value } : res;
          });

          set({
            results: {
              ...results,
              [category]: newResultCategory,
            },
            recents: newRecents,
          });
        },

        addRecents: ({ category, id }) => {
          const { recents, results } = get();
          const categoryResults = results[category];
          const toAdd = findResultToBeAddedToRecents(categoryResults, recents, id);
          const isAdded = textAddedToResults(recents, toAdd);

          if (isAdded) {
            const foundResult = recents.find((item) => item.id === id);
            const filteredResults = recents.filter((item) => item.id !== id);
            set({ recents: [{ ...foundResult, ...toAdd }, ...filteredResults] });
          } else {
            const newResult = {
              ...toAdd,
              id,
              category,
              rephrasedResults: [],
              type: ResultType.Recent,
            };

            recents.unshift(newResult);

            set({
              recents: [...recents],
            });
          }
        },

        showValidationError: (validationError) => {
          set({
            validationError,
          });
        },

        showGenericError: (genericError) => {
          clearTimeout(genericErrorTimeout);

          set({
            genericError,
          });

          if (genericError) {
            genericErrorTimeout = setTimeout(() => {
              set({
                genericError: '',
              });
            }, 10000);
            handleScroll('qt_main_content');
          }
        },

        setFormValues: (formValues) => {
          set({ formValues });
        },
        setFormValue: ({ name, value }) => {
          const { formValues } = get();

          set({ formValues: { ...formValues, [name]: value } });
        },
      };
    },
    {
      name: 'text-copywriter-store',
    },
  ),
);

export default useStore;
